// src/services/awsApiService.js
// AWS API Service - calls Lambda API with Cognito authentication

import { fetchAuthSession } from 'aws-amplify/auth';
import { API_BASE_URL } from '../lib/awsConfig';
import { demoERAPayments, demoPreAuths } from '../data/demoData';

const isDemoMode = (process.env.REACT_APP_MODE || '').trim() === 'demo' ||
  (typeof window !== 'undefined' && window.location.hostname === 'demo.flowrcmdental.com');

// Selected practice for org users (billing_admin, dso_admin)
let _selectedPracticeId = null;
export const setSelectedPracticeId = (id) => { _selectedPracticeId = id; };

// Get auth token for API calls — forceRefresh ensures we never send an expired token
async function getAuthToken(forceRefresh = false) {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('[awsApiService] Error getting auth token:', error);
    return null;
  }
}

// Generic API call helper — auto-refreshes token on 401/500 auth failures
async function apiCall(endpoint, options = {}, isRetry = false) {
  const token = await getAuthToken(isRetry);

  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(_selectedPracticeId && { 'x-practice-id': _selectedPracticeId }),
    ...options.headers
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    // On first 401, force-refresh the token and retry once
    if (!isRetry && response.status === 401) {
      return apiCall(endpoint, options, true);
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.details || errorData.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ==================== PATIENTS ====================

export const getPatients = async (practiceId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.search) params.append('search', options.search);
    if (options.archived) params.append('archived', options.archived);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const data = await apiCall(`/patients${queryString}`);

    return {
      success: true,
      data: data.patients || [],
      count: data.total || 0
    };
  } catch (error) {
    console.error('[awsApiService] Error fetching patients:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const getPatientById = async (patientId, practiceId) => {
  try {
    const data = await apiCall(`/patients/${patientId}`);
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error fetching patient:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const createPatient = async (patientData) => {
  try {
    const data = await apiCall('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error creating patient:', error);
    return { success: false, error: error.message };
  }
};

export const updatePatient = async (patientId, patientData) => {
  try {
    const data = await apiCall(`/patients/${patientId}`, {
      method: 'PUT',
      body: JSON.stringify(patientData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error updating patient:', error);
    return { success: false, error: error.message };
  }
};

export const deletePatient = async (patientId) => {
  try {
    await apiCall(`/patients/${patientId}`, { method: 'DELETE' });
    return { success: true };
  } catch (error) {
    console.error('[awsApiService] Error deleting patient:', error);
    return { success: false, error: error.message };
  }
};

export const archivePatient = async (patientId) => {
  try {
    const data = await apiCall(`/patients/${patientId}/archive`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error archiving patient:', error);
    return { success: false, error: error.message };
  }
};

export const restorePatient = async (patientId) => {
  try {
    const data = await apiCall(`/patients/${patientId}/restore`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error restoring patient:', error);
    return { success: false, error: error.message };
  }
};

// ==================== CLAIMS ====================

export const getClaims = async (practiceId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.status) params.append('status', options.status);
    if (options.archived) params.append('archived', options.archived);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const data = await apiCall(`/claims${queryString}`);

    return {
      success: true,
      data: data.claims || [],
      count: data.total || 0
    };
  } catch (error) {
    console.error('[awsApiService] Error fetching claims:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const getClaimById = async (claimId, practiceId) => {
  try {
    const data = await apiCall(`/claims/${claimId}`);
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error fetching claim:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const getClaimsByPatient = async (patientId, practiceId) => {
  try {
    const data = await apiCall(`/claims?patient_id=${patientId}`);
    return { success: true, data: data.claims || [] };
  } catch (error) {
    console.error('[awsApiService] Error fetching patient claims:', error);
    return { success: false, error: error.message, data: [] };
  }
};

export const createClaim = async (claimData) => {
  try {
    const data = await apiCall('/claims', {
      method: 'POST',
      body: JSON.stringify(claimData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error creating claim:', error);
    return { success: false, error: error.message };
  }
};

export const updateClaim = async (claimId, claimData) => {
  try {
    const data = await apiCall(`/claims/${claimId}`, {
      method: 'PUT',
      body: JSON.stringify(claimData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error updating claim:', error);
    return { success: false, error: error.message };
  }
};

export const archiveClaim = async (claimId) => {
  try {
    const data = await apiCall(`/claims/${claimId}/archive`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error archiving claim:', error);
    return { success: false, error: error.message };
  }
};

export const restoreClaim = async (claimId) => {
  try {
    const data = await apiCall(`/claims/${claimId}/restore`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error restoring claim:', error);
    return { success: false, error: error.message };
  }
};

export const sendAppealFax = async (claimId, faxNumber) => {
  try {
    const body = {};
    if (faxNumber) body.fax_number = faxNumber;
    const data = await apiCall(`/claims/${claimId}/send-appeal-fax`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const submitAppeal = async (claimId) => {
  try {
    const data = await apiCall(`/claims/${claimId}/submit-appeal`, { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error submitting appeal:', error);
    return { success: false, error: error.message };
  }
};

export const updateAppealStatus = async (claimId, appealStatus, paidAmount) => {
  try {
    const body = { appeal_status: appealStatus };
    if (paidAmount !== undefined && paidAmount !== null) {
      body.claim_paid_amount = paidAmount;
    }
    const data = await apiCall(`/claims/${claimId}/appeal-status`, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error updating appeal status:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ANALYTICS ====================

export const getAnalytics = async (practiceId) => {
  try {
    const data = await apiCall('/analytics');
    return {
      success: true,
      data: {
        patients: { total: data.patients || 0 },
        claims: {
          total: data.totalClaims || 0,
          pending: data.pendingClaims || 0,
          denied: data.deniedClaims || 0,
          paid: data.paidClaims || 0
        },
        revenue: {
          totalBilled: data.totalBilled || 0,
          totalPaid: data.totalCollected || 0,
          collectionRate: data.collectionRate || 0,
          denialRate: data.denialRate || 0
        }
      }
    };
  } catch (error) {
    console.error('[awsApiService] Error fetching analytics:', error);
    return { success: false, error: error.message, data: null };
  }
};

// ==================== USERS ====================

export const getUsers = async (practiceId) => {
  try {
    const data = await apiCall('/users');
    return { success: true, users: data.users || [] };
  } catch (error) {
    console.error('[awsApiService] Error fetching users:', error);
    return { success: false, error: error.message, users: [] };
  }
};

export const createUser = async (userData) => {
  try {
    const data = await apiCall('/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    return { success: true, user: data.user };
  } catch (error) {
    console.error('[awsApiService] Error creating user:', error);
    return { success: false, error: error.message };
  }
};

export const approveUser = async (userId) => {
  try {
    const data = await apiCall(`/users/${userId}/approve`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error approving user:', error);
    return { success: false, error: error.message };
  }
};

export const deactivateUser = async (userId) => {
  try {
    const data = await apiCall(`/users/${userId}/deactivate`, { method: 'PUT' });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error deactivating user:', error);
    return { success: false, error: error.message };
  }
};

export const updateUserRole = async (userId, role) => {
  try {
    const data = await apiCall(`/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error updating user role:', error);
    return { success: false, error: error.message };
  }
};

// ==================== PRACTICE ====================

export const getPractice = async () => {
  try {
    const data = await apiCall('/practice');
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error fetching practice:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const updatePractice = async (practiceData) => {
  try {
    const data = await apiCall('/practice', {
      method: 'PUT',
      body: JSON.stringify(practiceData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error updating practice:', error);
    return { success: false, error: error.message };
  }
};

// ==================== AUDIT LOGS ====================

export const getAuditLogs = async (practiceId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);

    const queryString = params.toString() ? `?${params.toString()}` : '';
    const data = await apiCall(`/audit-logs${queryString}`);

    return { success: true, logs: data.logs || [] };
  } catch (error) {
    console.error('[awsApiService] Error fetching audit logs:', error);
    return { success: false, error: error.message, logs: [] };
  }
};

export const createAuditLog = async (logData) => {
  try {
    const data = await apiCall('/audit-logs', {
      method: 'POST',
      body: JSON.stringify(logData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error creating audit log:', error);
    return { success: false, error: error.message };
  }
};

// ==================== AI WORKFLOWS ====================

export const analyzeDenial = async (denialData) => {
  try {
    const data = await apiCall('/ai/analyze-denial', {
      method: 'POST',
      body: JSON.stringify(denialData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error analyzing denial:', error);
    return { success: false, error: error.message };
  }
};

export const validateClaim = async (claimData) => {
  try {
    const data = await apiCall('/ai/validate-claim', {
      method: 'POST',
      body: JSON.stringify(claimData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error validating claim:', error);
    return { success: false, error: error.message };
  }
};

export const generateAppeal = async (appealData) => {
  try {
    const data = await apiCall('/ai/generate-appeal', {
      method: 'POST',
      body: JSON.stringify(appealData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error generating appeal:', error);
    return { success: false, error: error.message };
  }
};

export const generatePreAuth = async (preAuthData) => {
  try {
    const data = await apiCall('/ai/generate-preauth', {
      method: 'POST',
      body: JSON.stringify(preAuthData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error generating pre-auth:', error);
    return { success: false, error: error.message };
  }
};

export const medicalCoding = async (codingData) => {
  try {
    const data = await apiCall('/ai/medical-coding', {
      method: 'POST',
      body: JSON.stringify(codingData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error analyzing medical coding:', error);
    return { success: false, error: error.message };
  }
};

export const patientEngagement = async (patientData) => {
  try {
    const data = await apiCall('/ai/patient-engagement', {
      method: 'POST',
      body: JSON.stringify(patientData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error analyzing patient financials:', error);
    return { success: false, error: error.message };
  }
};

export const verifyBenefits = async (benefitsData) => {
  if (isDemoMode) {
    await new Promise(r => setTimeout(r, 1800));
    return {
      success: true,
      data: {
        InsuranceStatus: 'Active Coverage',
        PlanName: 'MetLife Dental PPO',
        GroupNumber: 'ML-GRP-4472',
        Deductible: '$50 individual / $150 family (met)',
        AnnualMaximum: '$2,000',
        RemainingBenefit: '$1,450 remaining',
      }
    };
  }
  try {
    const data = await apiCall('/verify-benefits', {
      method: 'POST',
      body: JSON.stringify(benefitsData)
    });
    return { success: true, data: data.data || data };
  } catch (error) {
    console.error('[awsApiService] Error verifying benefits:', error);
    return { success: false, error: error.message };
  }
};

// ==================== AUTOMATION ====================

export const scrubClaim = async (claimData) => {
  try {
    const data = await apiCall('/claims/scrub', { method: 'POST', body: JSON.stringify(claimData) });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getClaimAging = async () => {
  try {
    const data = await apiCall('/claims/aging', { method: 'GET' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAutomationActivity = async () => {
  try {
    const data = await apiCall('/automation/activity', { method: 'GET' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAllAutomationActivity = async (page = 1, limit = 25) => {
  try {
    const data = await apiCall(`/automation/activity/all?page=${page}&limit=${limit}`, { method: 'GET' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const runAutomation = async () => {
  try {
    const data = await apiCall('/automation/run', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== EMAIL ====================

export const sendEmail = async (emailData) => {
  try {
    const data = await apiCall('/email/send', {
      method: 'POST',
      body: JSON.stringify(emailData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error sending email:', error);
    return { success: false, error: error.message };
  }
};

// ==================== SEED DEMO DATA ====================

export const seedDemoData = async (reset = false) => {
  try {
    const data = await apiCall('/seed/demo-data', {
      method: 'POST',
      body: JSON.stringify({ reset })
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error seeding demo data:', error);
    return { success: false, error: error.message };
  }
};

// ==================== AI USAGE ====================

export const getAIUsage = async () => {
  try {
    const data = await apiCall('/ai/usage');
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error fetching AI usage:', error);
    return { success: false, error: error.message };
  }
};

// ==================== HEALTH CHECK ====================

export const healthCheck = async () => {
  try {
    const data = await apiCall('/health-check');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== BILLING ====================

export const clearStripeCustomer = async () => {
  try {
    const data = await apiCall('/billing/clear-customer', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createCheckoutSession = async (planId) => {
  try {
    const data = await apiCall('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({
        planId,
        successUrl: `${window.location.origin}/billing?success=true`,
        cancelUrl: `${window.location.origin}/billing?canceled=true`
      })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const createPortalSession = async () => {
  try {
    const data = await apiCall('/billing/portal', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getBillingStatus = async () => {
  try {
    const data = await apiCall('/billing/status');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== OCR ====================

export const captureOCR = async (documentData) => {
  try {
    const data = await apiCall('/ocr/capture', {
      method: 'POST',
      body: JSON.stringify(documentData)
    });
    return { success: true, data };
  } catch (error) {
    console.error('[awsApiService] Error capturing OCR:', error);
    return { success: false, error: error.message };
  }
};

// ==================== AUTOMATION SETTINGS ====================

export const getAutomationSettings = async () => {
  try {
    const data = await apiCall('/automation/settings');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveAutomationSettings = async (settings) => {
  try {
    const data = await apiCall('/automation/settings', {
      method: 'PUT',
      body: JSON.stringify({ settings })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== BILLING EXCEPTIONS ====================

export const getBillingExceptions = async () => {
  try {
    const data = await apiCall('/billing-exceptions');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveBillingExceptions = async (exceptions) => {
  try {
    const data = await apiCall('/billing-exceptions', {
      method: 'PUT',
      body: JSON.stringify({ exceptions })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ERA / Payment Posting
export const syncERAFromClearinghouse = async () => {
  try {
    const data = await apiCall('/era/sync', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getERAPayments = async () => {
  if (isDemoMode) return { success: true, data: demoERAPayments };
  try { const data = await apiCall('/era'); return { success: true, data: data.data || [] }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const postERAPayment = async (payload) => {
  try { const data = await apiCall('/era', { method: 'POST', body: JSON.stringify(payload) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const updateERAPayment = async (id, payload) => {
  try { const data = await apiCall(`/era/${id}`, { method: 'PUT', body: JSON.stringify(payload) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const applyERAPayment = async (id) => {
  try { const data = await apiCall(`/era/${id}/apply`, { method: 'POST' }); return { success: true, ...data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const resolveERAException = async (eraId, claimId, paidAmount, exceptionRef) => {
  try {
    const data = await apiCall(`/era/${eraId}/resolve-exception`, {
      method: 'PUT',
      body: JSON.stringify({ claim_id: claimId, paid_amount: paidAmount, exception_ref: exceptionRef })
    });
    return { success: true, ...data };
  } catch (error) { return { success: false, error: error.message }; }
};

// Notifications
export const getNotifications = async () => {
  try { const data = await apiCall('/notifications'); return { success: true, data: data.data || [], unreadCount: data.unreadCount || 0 }; }
  catch (error) { return { success: false, error: error.message, data: [], unreadCount: 0 }; }
};
export const markNotificationRead = async (id) => {
  try { await apiCall(`/notifications/${id}/read`, { method: 'PUT' }); return { success: true }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const markAllNotificationsRead = async () => {
  try { await apiCall('/notifications/read-all', { method: 'PUT' }); return { success: true }; }
  catch (error) { return { success: false, error: error.message }; }
};

// Patient Statements
export const getStatements = async () => {
  try { const data = await apiCall('/statements'); return { success: true, data: data.data || [] }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const generateStatement = async (payload) => {
  try { const data = await apiCall('/statements/generate', { method: 'POST', body: JSON.stringify(payload) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const sendStatement = async (id) => {
  try { const data = await apiCall(`/statements/${id}/send`, { method: 'POST' }); return { success: true, message: data.message }; }
  catch (error) { return { success: false, error: error.message }; }
};

// Pre-Auth Tracking
export const getPreAuths = async (status) => {
  if (isDemoMode) {
    const filtered = status ? demoPreAuths.filter(p => p.status === status) : demoPreAuths;
    return { success: true, data: filtered };
  }
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  try { const data = await apiCall(`/preauth${qs}`); return { success: true, data: data.data || [] }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const createPreAuth = async (payload) => {
  try { const data = await apiCall('/preauth', { method: 'POST', body: JSON.stringify(payload) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const updatePreAuth = async (id, payload) => {
  try { const data = await apiCall(`/preauth/${id}`, { method: 'PUT', body: JSON.stringify(payload) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};
export const submitPreAuth = async (id) => {
  try {
    const data = await apiCall(`/preauth/${id}/submit`, { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const syncPreAuthResponses = async () => {
  try {
    const data = await apiCall('/preauth/sync', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deletePreAuth = async (id) => {
  try { await apiCall(`/preauth/${id}`, { method: 'DELETE' }); return { success: true }; }
  catch (error) { return { success: false, error: error.message }; }
};

// Office Ally
export const submitClaimToOfficeAlly = async (claimId, dryRun = false) => {
  try {
    const data = await apiCall('/office-ally/submit', { method: 'POST', body: JSON.stringify({ claim_id: claimId, dry_run: dryRun }) });
    return { success: true, ...data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== ORGANIZATION ====================

export const getOrg = async () => {
  try { const data = await apiCall('/org'); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};

export const createOrg = async (orgData) => {
  try { const data = await apiCall('/org', { method: 'POST', body: JSON.stringify(orgData) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};

export const getOrgPractices = async () => {
  try { const data = await apiCall('/org/practices'); return { success: true, data: data.data || [] }; }
  catch (error) { return { success: false, error: error.message, data: [] }; }
};

export const createOrgPractice = async (practiceData) => {
  try { const data = await apiCall('/org/practices', { method: 'POST', body: JSON.stringify(practiceData) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};

export const updateOrgPractice = async (id, practiceData) => {
  try { const data = await apiCall(`/org/practices/${id}`, { method: 'PUT', body: JSON.stringify(practiceData) }); return { success: true, data: data.data }; }
  catch (error) { return { success: false, error: error.message }; }
};

export const inviteOrgUser = async (payload) => {
  try { const data = await apiCall('/org/invite', { method: 'POST', body: JSON.stringify(payload) }); return { success: true, data }; }
  catch (error) { return { success: false, error: error.message }; }
};

// ==================== STRIPE CONNECT / PATIENT PAYMENTS ====================

export const connectOnboard = async () => {
  try {
    const data = await apiCall('/connect/onboard', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const connectStatus = async () => {
  try {
    const data = await apiCall('/connect/status');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const connectDashboard = async () => {
  try {
    const data = await apiCall('/connect/dashboard', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const connectDisconnect = async () => {
  try {
    const data = await apiCall('/connect/disconnect', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const sendPaymentLink = async (payload) => {
  try {
    const data = await apiCall('/payments/send-link', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== AVAILITY CONFIG ====================

export const getAvailityConfig = async () => {
  try {
    const data = await apiCall('/practice/availity-config');
    return { success: true, data: data.config || {} };
  } catch (error) {
    return { success: false, error: error.message, data: {} };
  }
};

export const saveAvailityConfig = async (config) => {
  try {
    const data = await apiCall('/practice/availity-config', {
      method: 'PUT',
      body: JSON.stringify({ config })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== CLEARINGHOUSE SETTINGS ====================

export const getClearinghouseSettings = async () => {
  try {
    const data = await apiCall('/practice/clearinghouse');
    return { success: true, data: data.config || {} };
  } catch (error) {
    return { success: false, error: error.message, data: {} };
  }
};

export const saveClearinghouseSettings = async (config) => {
  try {
    const data = await apiCall('/practice/clearinghouse', {
      method: 'PUT',
      body: JSON.stringify({ config })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const testClearinghouseConnection = async () => {
  try {
    const data = await apiCall('/practice/clearinghouse/test', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== OPEN DENTAL SYNC ====================

export const getOpenDentalConfig = async () => {
  try { return await apiCall('/open-dental/config'); }
  catch (error) { return { success: false, error: error.message }; }
};

export const saveOpenDentalConfig = async (customerKey) => {
  try { return await apiCall('/open-dental/config', { method: 'PUT', body: JSON.stringify({ customer_key: customerKey }) }); }
  catch (error) { return { success: false, error: error.message }; }
};

export const testOpenDentalConnection = async (customerKey = null) => {
  try { return await apiCall('/open-dental/test', { method: 'POST', body: JSON.stringify(customerKey ? { customer_key: customerKey } : {}) }); }
  catch (error) { return { success: false, error: error.message }; }
};

export const syncOpenDental = async (customerKey = null) => {
  try { return await apiCall('/open-dental/sync', { method: 'POST', body: JSON.stringify(customerKey ? { customer_key: customerKey } : {}) }); }
  catch (error) { return { success: false, error: error.message }; }
};

export const syncOpenDentalData = async (type = 'both') => {
  try { return await syncOpenDental(); }
  catch (error) { return { success: false, error: error.message }; }
};

// ==================== PMS BOT CONFIG ====================

export const getPmsBotConfig = async () => {
  try {
    const data = await apiCall('/pms/config');
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message, data: {} };
  }
};

export const savePmsBotConfig = async (system, username, password, portalUrl) => {
  try {
    const data = await apiCall('/pms/config', {
      method: 'POST',
      body: JSON.stringify({ system, username, password, portalUrl: portalUrl || null })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Onboarding
export const savePracticeProfile = async (profile) => {
  try {
    const data = await apiCall('/practice/profile', { method: 'POST', body: JSON.stringify(profile) });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const markSetupComplete = async () => {
  try {
    const data = await apiCall('/practice/setup-complete', { method: 'POST' });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== DOCUMENT STORAGE ====================

export const uploadDocument = async ({ claimId, patientId, documentType, fileName, fileData, mimeType, notes }) => {
  try {
    const data = await apiCall('/documents/upload', {
      method: 'POST',
      body: JSON.stringify({ claim_id: claimId, patient_id: patientId, document_type: documentType, file_name: fileName, file_data: fileData, mime_type: mimeType, notes })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const listClaimDocuments = async (claimId) => {
  try {
    const data = await apiCall(`/claims/${claimId}/documents`);
    return { success: true, documents: data.documents || [] };
  } catch (error) {
    return { success: false, error: error.message, documents: [] };
  }
};

export const getDocumentUrl = async (docId) => {
  try {
    const data = await apiCall(`/documents/${docId}/url`);
    return { success: true, url: data.url, document: data.document };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteDocument = async (docId) => {
  try {
    await apiCall(`/documents/${docId}`, { method: 'DELETE' });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const listDocuments = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);
    if (options.claimId) params.append('claim_id', options.claimId);
    if (options.patientId) params.append('patient_id', options.patientId);
    const data = await apiCall(`/documents?${params.toString()}`);
    return { success: true, documents: data.documents || [], total: data.total || 0 };
  } catch (error) {
    return { success: false, error: error.message, documents: [], total: 0 };
  }
};

export const writeOffClaim = async (claimId, writeoffAmount, reason) => {
  try {
    const data = await apiCall(`/claims/${claimId}/writeoff`, {
      method: 'PUT',
      body: JSON.stringify({ writeoff_amount: writeoffAmount, reason })
    });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== PAYERS ====================

export const getPayers = async (search = '') => {
  try {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const data = await apiCall(`/payers${qs}`);
    return { success: true, payers: data.payers || [] };
  } catch (error) {
    return { success: false, payers: [], error: error.message };
  }
};

export const createPayer = async (payload) => {
  try {
    const data = await apiCall('/payers', { method: 'POST', body: JSON.stringify(payload) });
    return { success: true, payer: data.payer };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updatePayer = async (id, payload) => {
  try {
    const data = await apiCall(`/payers/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
    return { success: true, ...data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const verifyPayer = async (id, verified = true) => {
  try {
    const data = await apiCall(`/payers/${id}/verify`, { method: 'PUT', body: JSON.stringify({ verified }) });
    return { success: true, ...data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deletePayer = async (id) => {
  try {
    await apiCall(`/payers/${id}`, { method: 'DELETE' });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

